import React, { useState, useEffect , useCallback} from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { toast } from 'sonner';
import { getUser } from '../../utils/auth';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Building2,
  GraduationCap,
  Award,
  Camera,
  Save,
  Bell,
  Shield,
  Globe,
  Clock,
  Stethoscope,
  X,
  Check,
  ChevronsUpDown
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const SPECIALTIES_OPTIONS = [
  'Cardiology', 'Orthopedics', 'Neurology', 'Oncology', 'Gastroenterology',
  'Pulmonology', 'Nephrology', 'Urology', 'Ophthalmology', 'ENT',
  'Dermatology', 'Psychiatry', 'Pediatrics', 'Gynecology', 'Obstetrics',
  'General Surgery', 'Plastic Surgery', 'Dental', 'Fertility & IVF',
  'Bariatric Surgery', 'Spine Surgery', 'Transplant', 'Ayurveda'
];

const LANGUAGES_OPTIONS = [
  'English', 'Hindi', 'Arabic', 'Russian', 'French', 'German', 
  'Spanish', 'Mandarin', 'Bengali', 'Tamil', 'Telugu', 'Kannada', 'Malayalam'
];

const QUALIFICATION_OPTIONS = [
  'MBBS', 'MD', 'MS', 'DM', 'MCh', 'DNB', 'FRCS', 'MRCP', 'PhD',
  'Fellowship', 'BDS', 'MDS', 'BAMS', 'BHMS'
];

const CERTIFICATION_OPTIONS = [
  'Board Certified', 'FACC', 'FACS', 'FRCP', 'ESC Member', 
  'AHA Certified', 'ACLS Certified', 'BLS Certified'
];

const AVAILABILITY_OPTIONS = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
];

export default function DoctorSettings() {
  const [user] = useState(getUser);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hospitals, setHospitals] = useState([]);
  const [hospitalSearchOpen, setHospitalSearchOpen] = useState(false);
  const [newAward, setNewAward] = useState('');
  const [newResearchInterest, setNewResearchInterest] = useState('');
  
  const [profile, setProfile] = useState({
    fullName: user?.full_name || '',
    email: user?.email || '',
    phone: '',
    specialization: '',
    sub_specializations: [],
    experience_years: '',
    qualifications: [],
    medical_school: '',
    certifications: [],
    registration_number: '',
    hospital_affiliations: [],
    consultation_fee: '',
    consultation_timings: '',
    availability: [],
    emergency_available: false,
    languages: ['English'],
    awards: [],
    research_interests: [],
    about: '',
    city: '',
    state: ''
  });

  const [notifications, setNotifications] = useState({
    emailInquiries: true,
    emailAppointments: true,
    emailReviews: true,
    smsAppointments: true,
    smsUrgent: true
  });


  const fetchProfile = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/doctor/profile`, {
        headers: { }
      });

      if (response.ok) {
        const data = await response.json();
        setProfile({
          fullName: data.profile?.full_name || user?.full_name || '',
          email: data.profile?.email || user?.email || '',
          phone: data.profile?.phone || '',
          specialization: data.profile?.specialization || '',
          sub_specializations: data.profile?.sub_specializations || [],
          experience_years: data.profile?.experience_years?.toString() || '',
          qualifications: data.profile?.qualifications || [],
          medical_school: data.profile?.medical_school || '',
          certifications: data.profile?.certifications || [],
          registration_number: data.profile?.registration_number || '',
          hospital_affiliations: data.profile?.hospital_affiliations || [],
          consultation_fee: data.profile?.consultation_fee?.toString() || '',
          consultation_timings: data.profile?.consultation_timings || '',
          availability: data.profile?.availability || [],
          emergency_available: data.profile?.emergency_available || false,
          languages: data.profile?.languages || ['English'],
          awards: data.profile?.awards || [],
          research_interests: data.profile?.research_interests || [],
          about: data.profile?.about || data.profile?.bio || '',
          city: data.profile?.city || '',
          state: data.profile?.state || ''
        });
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, [user]);

  const fetchHospitals = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/public/hospitals/names`);
      if (response.ok) {
        const data = await response.json();
        setHospitals(data.hospitals || []);
      }
    } catch (err) {
    }
  }, []);

  useEffect(() => {
    fetchProfile();
    fetchHospitals();
  }, [fetchProfile, fetchHospitals]);

  const toggleArrayItem = (field, item) => {
    const currentItems = profile[field] || [];
    if (currentItems.includes(item)) {
      setProfile({ ...profile, [field]: currentItems.filter(i => i !== item) });
    } else {
      setProfile({ ...profile, [field]: [...currentItems, item] });
    }
  };

  const addToArray = (field, value, setter) => {
    if (value.trim()) {
      const currentItems = profile[field] || [];
      if (!currentItems.includes(value.trim())) {
        setProfile({ ...profile, [field]: [...currentItems, value.trim()] });
      }
      setter('');
    }
  };

  const removeFromArray = (field, index) => {
    const currentItems = [...(profile[field] || [])];
    currentItems.splice(index, 1);
    setProfile({ ...profile, [field]: currentItems });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE}/api/doctor/profile`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          full_name: profile.fullName,
          email: profile.email,
          phone: profile.phone,
          specialization: profile.specialization,
          sub_specializations: profile.sub_specializations,
          experience_years: parseInt(profile.experience_years) || 0,
          qualifications: profile.qualifications,
          medical_school: profile.medical_school,
          certifications: profile.certifications,
          registration_number: profile.registration_number,
          hospital_affiliations: profile.hospital_affiliations,
          consultation_fee: parseFloat(profile.consultation_fee) || 0,
          consultation_timings: profile.consultation_timings,
          availability: profile.availability,
          emergency_available: profile.emergency_available,
          languages: profile.languages,
          awards: profile.awards,
          research_interests: profile.research_interests,
          about: profile.about,
          bio: profile.about,
          city: profile.city,
          state: profile.state
        })
      });

      if (response.ok) {
        toast.success('Profile updated successfully!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save changes');
      }
    } catch (error) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const renderTagSelector = (field, options, label) => (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="border rounded-lg p-3 max-h-32 overflow-y-auto bg-gray-50">
        <div className="flex flex-wrap gap-2">
          {options.map(option => (
            <Badge
              key={option}
              variant={profile[field]?.includes(option) ? 'default' : 'outline'}
              className={`cursor-pointer text-xs transition-colors ${
                profile[field]?.includes(option) 
                  ? 'bg-teal-600 text-white hover:bg-teal-700' 
                  : 'hover:bg-gray-100'
              }`}
              onClick={() => toggleArrayItem(field, option)}
            >
              {option}
              {profile[field]?.includes(option) && <X className="w-3 h-3 ml-1" />}
            </Badge>
          ))}
        </div>
      </div>
      {profile[field]?.length > 0 && (
        <p className="text-xs text-gray-500">{profile[field].length} selected</p>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-6" data-testid="doctor-settings-loading">
        <div className="h-8 bg-gray-200 rounded animate-pulse w-1/4"></div>
        <div className="h-64 bg-gray-200 rounded animate-pulse"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="doctor-settings">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Profile Settings</h1>
        <p className="text-gray-500 text-sm mt-1">Manage your professional profile and preferences</p>
      </div>

      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="grid w-full grid-cols-3 max-w-md">
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile" className="space-y-6 mt-6">
          {/* Profile Header Card */}
          <Card className="border shadow-sm">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <div className="relative">
                  <Avatar className="h-24 w-24 border-4 border-teal-500">
                    <AvatarImage src={user?.avatar} />
                    <AvatarFallback className="bg-teal-100 text-teal-700 text-2xl">
                      {profile.fullName.split(' ').slice(-1).map(n => n[0]).join('')}
                    </AvatarFallback>
                  </Avatar>
                  <button className="absolute bottom-0 right-0 w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center hover:bg-teal-700">
                    <Camera className="w-4 h-4" />
                  </button>
                </div>
                <div className="text-center sm:text-left">
                  <h2 className="text-xl font-bold text-gray-900">{profile.fullName}</h2>
                  <p className="text-gray-500">{profile.specialization || 'Specialization not set'}</p>
                  <div className="flex flex-wrap gap-2 mt-2 justify-center sm:justify-start">
                    <Badge className="bg-green-100 text-green-700 border-green-200">Verified</Badge>
                    {profile.experience_years && (
                      <Badge className="bg-teal-100 text-teal-700 border-teal-200">{profile.experience_years}+ Years Exp</Badge>
                    )}
                    {profile.emergency_available && (
                      <Badge className="bg-red-100 text-red-700 border-red-200">Emergency Available</Badge>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Basic Information */}
          <Card className="border shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="w-5 h-5 text-teal-600" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Full Name</Label>
                  <Input 
                    value={profile.fullName} 
                    onChange={(e) => setProfile({...profile, fullName: e.target.value})}
                    placeholder="Dr. John Doe"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input 
                    type="email" 
                    value={profile.email} 
                    onChange={(e) => setProfile({...profile, email: e.target.value})}
                    placeholder="doctor@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input 
                    value={profile.phone} 
                    onChange={(e) => setProfile({...profile, phone: e.target.value})}
                    placeholder="+91 98765 43210"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Consultation Fee (INR)</Label>
                  <Input 
                    type="number" 
                    value={profile.consultation_fee} 
                    onChange={(e) => setProfile({...profile, consultation_fee: e.target.value})}
                    placeholder="2000"
                  />
                </div>
                <div className="space-y-2">
                  <Label>City</Label>
                  <Input 
                    value={profile.city} 
                    onChange={(e) => setProfile({...profile, city: e.target.value})}
                    placeholder="Mumbai"
                  />
                </div>
                <div className="space-y-2">
                  <Label>State</Label>
                  <Input 
                    value={profile.state} 
                    onChange={(e) => setProfile({...profile, state: e.target.value})}
                    placeholder="Maharashtra"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Professional Information */}
          <Card className="border shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Stethoscope className="w-5 h-5 text-teal-600" />
                Professional Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Primary Specialization</Label>
                  <select
                    value={profile.specialization}
                    onChange={(e) => setProfile({...profile, specialization: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="">Select Specialization</option>
                    {SPECIALTIES_OPTIONS.map(spec => (
                      <option key={spec} value={spec}>{spec}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Years of Experience</Label>
                  <Input 
                    type="number" 
                    value={profile.experience_years} 
                    onChange={(e) => setProfile({...profile, experience_years: e.target.value})}
                    placeholder="15"
                  />
                </div>
              </div>

              {renderTagSelector('languages', LANGUAGES_OPTIONS, 'Languages Spoken')}

              <div className="space-y-2">
                <Label>Professional Bio</Label>
                <Textarea 
                  rows={4} 
                  value={profile.about} 
                  onChange={(e) => setProfile({...profile, about: e.target.value})}
                  placeholder="Tell patients about your experience and approach to care..."
                />
              </div>
            </CardContent>
          </Card>

          {/* Education & Qualifications */}
          <Card className="border shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-teal-600" />
                Education & Qualifications
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {renderTagSelector('qualifications', QUALIFICATION_OPTIONS, 'Qualifications')}
              
              <div className="space-y-2">
                <Label>Medical School / University</Label>
                <Input 
                  value={profile.medical_school} 
                  onChange={(e) => setProfile({...profile, medical_school: e.target.value})}
                  placeholder="All India Institute of Medical Sciences, New Delhi"
                />
              </div>

              <div className="space-y-2">
                <Label>Registration Number</Label>
                <Input 
                  value={profile.registration_number} 
                  onChange={(e) => setProfile({...profile, registration_number: e.target.value})}
                  placeholder="MCI-12345"
                />
              </div>

              {renderTagSelector('certifications', CERTIFICATION_OPTIONS, 'Certifications & Board Memberships')}
            </CardContent>
          </Card>

          {/* Hospital Affiliations */}
          <Card className="border shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="w-5 h-5 text-teal-600" />
                Hospital Affiliations
              </CardTitle>
              <CardDescription>
                Select hospitals where you practice (only registered hospitals shown)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Popover open={hospitalSearchOpen} onOpenChange={setHospitalSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between"
                  >
                    {profile.hospital_affiliations.length > 0 
                      ? `${profile.hospital_affiliations.length} hospital(s) selected`
                      : 'Search and select hospitals...'}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search hospitals..." />
                    <CommandList>
                      <CommandEmpty>No hospitals found.</CommandEmpty>
                      <CommandGroup className="max-h-48 overflow-y-auto">
                        {hospitals.map(hospital => (
                          <CommandItem
                            key={hospital._id}
                            value={hospital.name}
                            onSelect={() => {
                              toggleArrayItem('hospital_affiliations', hospital.name);
                            }}
                          >
                            <Check
                              className={`mr-2 h-4 w-4 ${
                                profile.hospital_affiliations.includes(hospital.name) ? 'opacity-100' : 'opacity-0'
                              }`}
                            />
                            <div className="flex flex-col">
                              <span className="text-sm">{hospital.name}</span>
                              {hospital.city && (
                                <span className="text-xs text-gray-500">{hospital.city}, {hospital.state}</span>
                              )}
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              {profile.hospital_affiliations.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {profile.hospital_affiliations.map((hospital, index) => (
                    <Badge
                      key={index}
                      className="bg-teal-600 text-white cursor-pointer hover:bg-teal-700"
                      onClick={() => removeFromArray('hospital_affiliations', index)}
                    >
                      {hospital} <X className="w-3 h-3 ml-1" />
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Availability */}
          <Card className="border shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-teal-600" />
                Availability
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {renderTagSelector('availability', AVAILABILITY_OPTIONS, 'Available Days')}

              <div className="space-y-2">
                <Label>Consultation Timings</Label>
                <Input 
                  value={profile.consultation_timings} 
                  onChange={(e) => setProfile({...profile, consultation_timings: e.target.value})}
                  placeholder="10:00 AM - 1:00 PM, 4:00 PM - 7:00 PM"
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <span className="font-medium">Available for Emergency Consultations</span>
                  <p className="text-sm text-gray-500">You may be contacted for urgent cases outside regular hours</p>
                </div>
                <Switch
                  checked={profile.emergency_available}
                  onCheckedChange={(checked) => setProfile({...profile, emergency_available: checked})}
                />
              </div>
            </CardContent>
          </Card>

          {/* Awards & Research */}
          <Card className="border shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Award className="w-5 h-5 text-teal-600" />
                Awards & Research
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Awards */}
              <div className="space-y-2">
                <Label>Awards & Recognition</Label>
                <div className="flex gap-2">
                  <Input
                    value={newAward}
                    onChange={(e) => setNewAward(e.target.value)}
                    placeholder="Best Doctor Award 2023"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToArray('awards', newAward, setNewAward))}
                  />
                  <Button
                    type="button"
                    onClick={() => addToArray('awards', newAward, setNewAward)}
                    className="bg-teal-600 hover:bg-teal-700"
                  >
                    Add
                  </Button>
                </div>
                {profile.awards.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {profile.awards.map((award, index) => (
                      <Badge
                        key={index}
                        className="bg-amber-100 text-amber-800 cursor-pointer hover:bg-amber-200"
                        onClick={() => removeFromArray('awards', index)}
                      >
                        {award} <X className="w-3 h-3 ml-1" />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Research Interests */}
              <div className="space-y-2">
                <Label>Research Interests</Label>
                <div className="flex gap-2">
                  <Input
                    value={newResearchInterest}
                    onChange={(e) => setNewResearchInterest(e.target.value)}
                    placeholder="Minimally Invasive Surgery"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToArray('research_interests', newResearchInterest, setNewResearchInterest))}
                  />
                  <Button
                    type="button"
                    onClick={() => addToArray('research_interests', newResearchInterest, setNewResearchInterest)}
                    className="bg-teal-600 hover:bg-teal-700"
                  >
                    Add
                  </Button>
                </div>
                {profile.research_interests.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {profile.research_interests.map((interest, index) => (
                      <Badge
                        key={index}
                        className="bg-blue-100 text-blue-800 cursor-pointer hover:bg-blue-200"
                        onClick={() => removeFromArray('research_interests', index)}
                      >
                        {interest} <X className="w-3 h-3 ml-1" />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="mt-6">
          <Card className="border shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Bell className="w-5 h-5 text-teal-600" />
                Notification Preferences
              </CardTitle>
              <CardDescription>Choose how you want to be notified</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900">Email Notifications</h3>
                <div className="space-y-3">
                  {[
                    { key: 'emailInquiries', label: 'New patient inquiries' },
                    { key: 'emailAppointments', label: 'Appointment confirmations and reminders' },
                    { key: 'emailReviews', label: 'New patient reviews' },
                  ].map(item => (
                    <div key={item.key} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{item.label}</span>
                      <Switch
                        checked={notifications[item.key]}
                        onCheckedChange={(checked) => setNotifications({...notifications, [item.key]: checked})}
                      />
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900">SMS Notifications</h3>
                <div className="space-y-3">
                  {[
                    { key: 'smsAppointments', label: 'Appointment reminders' },
                    { key: 'smsUrgent', label: 'Urgent patient messages' },
                  ].map(item => (
                    <div key={item.key} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{item.label}</span>
                      <Switch
                        checked={notifications[item.key]}
                        onCheckedChange={(checked) => setNotifications({...notifications, [item.key]: checked})}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security" className="mt-6">
          <Card className="border shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="w-5 h-5 text-teal-600" />
                Security Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Current Password</Label>
                  <Input type="password" placeholder="Enter current password" />
                </div>
                <div className="space-y-2">
                  <Label>New Password</Label>
                  <Input type="password" placeholder="Enter new password" />
                </div>
                <div className="space-y-2">
                  <Label>Confirm New Password</Label>
                  <Input type="password" placeholder="Confirm new password" />
                </div>
                <Button className="bg-teal-600 hover:bg-teal-700">
                  Update Password
                </Button>
              </div>

              <div className="border-t pt-6">
                <h3 className="font-medium text-gray-900 mb-4">Two-Factor Authentication</h3>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Add an extra layer of security to your account</p>
                    <p className="text-xs text-gray-400 mt-1">Currently: Disabled</p>
                  </div>
                  <Button variant="outline">Enable 2FA</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
